import { Modal } from 'components/Modal';
import { Title } from 'components/Title';
import styles from './CreateAlertsModal.module.scss';
import { Label } from 'components/CreateOrderForm';
import { useState, useEffect, useCallback } from 'react';
import { Textarea, TextInput } from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { ReactComponent as IconCalendar } from './img/iconCalendar.svg';
import { Button } from 'ui/Button';
import dayjs from 'dayjs';
import { TimeDropdown } from './TimeDropdown';
import {
  useCreateAlertWorkSupportMutation,
  useUpdateAlertWorkSupportMutation,
} from 'redux/Api/alertsWorkSupportApi/alertsWorkSupportApi';
import { InitialDataType } from 'pages/AlertsWorkSupport';

const getInitialValues = () => ({
  id: null,
  title: '',
  text: '',
  start_at: null,
  end_at: null,
  created_at: null,
});

export const CreateAlertsModal = ({
  setOpen,
  initialData,
  setInitialData,
  buttonCreateAlert,
  setButtonCreateAlert,
}: {
  setOpen: (open: boolean) => void;
  initialData?: InitialDataType | null;
  currentId?: number | null;
  onSuccess?: () => void;
  setInitialData?: (data: any | null) => void;
  buttonCreateAlert?: boolean;
  setButtonCreateAlert?: (value: boolean) => void;
}) => {
  const [values, setValues] = useState<any>(
    initialData
      ? {
          ...initialData,
          start_at: initialData.start_at
            ? dayjs(initialData.start_at).format('YYYY-MM-DD HH:mm')
            : null,
          end_at: initialData.end_at
            ? dayjs(initialData.end_at).format('YYYY-MM-DD HH:mm')
            : null,
          created_at: null,
        }
      : getInitialValues(),
  );
  const [createAlertWorkSupport] = useCreateAlertWorkSupportMutation();
  const [updateAlertWorkSupport] = useUpdateAlertWorkSupportMutation();
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [startTime, setStartTime] = useState<string>('');
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [endTime, setEndTime] = useState<string>('');
  const [errorTitle, setErrorTitle] = useState(false);
  const [errorSubTitle, setErrorSubTitle] = useState(false);
  const [errorTime, setErrorTime] = useState(false);

  const handleSave = async () => {
    const { end_at, start_at, text, title } = values;

    try {
      await createAlertWorkSupport({
        end_at,
        start_at,
        text,
        title,
      }).then(() => {
        setValues(getInitialValues());
        setInitialData?.(null);
        setOpen(false);
      });
    } catch (error) {
      console.log('Ошибка сохранения оповещения', error);
    }
  };

  const handleUpdate = async () => {
    const { end_at, start_at, text, title } = values;
    const id = initialData?.id;

    try {
      await updateAlertWorkSupport({
        id: String(id),
        body: {
          end_at,
          start_at,
          text,
          title,
        },
      }).then(() => {
        setValues(getInitialValues());
        setInitialData?.(null);
        setOpen(false);
      });
    } catch (error) {
      console.log('Ошибка сохранения оповещения', error);
    }
  };

  const resetForm = useCallback(() => {
    const defaultValues = getInitialValues();
    setValues(defaultValues);
    setStartDate(null);
    setStartTime('');
    setEndDate(null);
    setEndTime('');
    setInitialData?.(null);
  }, [setInitialData]);

  const handleClose = useCallback(
    (open: boolean) => {
      if (!open) {
        resetForm();
      }
      setOpen(open);
      setButtonCreateAlert?.(false);
    },
    [resetForm, setOpen],
  );

  const handleCancel = () => {
    handleClose(false);
  };

  const dateAndTimeFormatStart = (timeOverride?: string, dateOverride?: Date | null) => {
    const date = dateOverride !== undefined ? dateOverride : startDate;
    const time = timeOverride !== undefined ? timeOverride : startTime;
    if (!date || !time) {
      setValues((prev: any) => ({ ...prev, start_at: null }));
      return null;
    }
    const dateStr = dayjs(date).format('YYYY-MM-DD');
    const timeStr = time.length > 5 ? time.slice(0, 5) : time;
    const newValue = `${dateStr} ${timeStr}`;
    setValues((prev: any) => ({ ...prev, start_at: newValue }));
    return newValue;
  };

  const dateAndTimeFormatEnd = (timeOverride?: string, dateOverride?: Date | null) => {
    const date = dateOverride !== undefined ? dateOverride : endDate;
    const time = timeOverride !== undefined ? timeOverride : endTime;
    if (!date || !time) {
      setValues((prev: any) => ({ ...prev, end_at: null }));
      return null;
    }
    const dateStr = dayjs(date).format('YYYY-MM-DD');
    const timeStr = time.length > 5 ? time.slice(0, 5) : time;
    const newValue = `${dateStr} ${timeStr}`;
    setValues((prev: any) => ({ ...prev, end_at: newValue }));
    return newValue;
  };

  useEffect(() => {
    if (initialData) {
      setValues({
        ...initialData,
        start_at: initialData.start_at
          ? dayjs(initialData.start_at).format('YYYY-MM-DD HH:mm')
          : null,
        end_at: initialData.end_at
          ? dayjs(initialData.end_at).format('YYYY-MM-DD HH:mm')
          : null,
      });
      if (initialData.start_at) {
        const startDateTime = dayjs(initialData.start_at);
        setStartDate(startDateTime.toDate());
        setStartTime(startDateTime.format('HH:mm'));
      } else {
        setStartDate(null);
        setStartTime('');
      }
      if (initialData.end_at) {
        const endDateTime = dayjs(initialData.end_at);
        setEndDate(endDateTime.toDate());
        setEndTime(endDateTime.format('HH:mm'));
      } else {
        setEndDate(null);
        setEndTime('');
      }
    } else {
      const now = dayjs();
      const today = now.toDate();
      const currentTime = now.format('HH:mm');
      const dateStartValue = `${now.format('YYYY-MM-DD')} ${currentTime}`;

      const defaultValues = getInitialValues();
      setValues({
        ...defaultValues,
        start_at: dateStartValue,
      });
      setStartDate(today);
      setStartTime(currentTime);
      setEndDate(null);
      setEndTime('');
    }
  }, [initialData]);

  return (
    <>
      <Modal setOpen={handleClose}>
        <div className={styles.root}>
          <Title title='Создать оповещение' />

          <div>
            <Label text='Заголовок' />
            <Textarea
              value={values?.title || ''}
              onChange={(event) =>
                setValues((prev: any) => ({ ...prev, title: event.target.value }))
              }
              placeholder='Укажите название'
              required
              autosize
              minRows={1}
              maxRows={4}
              styles={{
                input: {
                  fontSize: '16px',
                  border: `${errorTitle ? '1px solid red' : 'none'}`,
                  display: 'flex',
                  alignItems: 'center',
                  paddingTop: '12px',
                  paddingBottom: '12px',
                },
              }}
            />
            {errorTitle && <span style={{ color: 'red' }}>Заполни заголовок</span>}
          </div>
          <div>
            <Label text='Тело сообщения' />
            <Textarea
              placeholder='Введи текст'
              withErrorStyles={false}
              value={values?.text || ''}
              onChange={(event) =>
                setValues((prev: any) => ({ ...prev, text: event.target.value }))
              }
              autosize
              minRows={1}
              maxRows={4}
              styles={{
                input: {
                  fontSize: '16px',
                  border: `${errorSubTitle ? '1px solid red' : 'none'}`,
                  display: 'flex',
                  alignItems: 'center',
                  paddingTop: '12px',
                  paddingBottom: '12px',
                },
              }}
            />
            {errorSubTitle && (
              <span style={{ color: 'red' }}>Заполни тело сообщения</span>
            )}
          </div>

          <div className={styles.containerDate}>
            <div>
              <div className={styles.dateBox}>
                <div style={{ flex: 1 }}>
                  <Label text='Дата начала' />
                  <DateInput
                    value={startDate}
                    onChange={(value) => {
                      setStartDate(value);
                      if (value && !startTime) {
                        setStartTime('00:00');
                        dateAndTimeFormatStart('00:00', value);
                      } else {
                        dateAndTimeFormatStart(undefined, value);
                      }
                    }}
                    placeholder={'Не выбрана'}
                    rightSection={<IconCalendar />}
                    styles={{
                      input: {
                        height: '48px',
                        fontSize: '16px',
                        border: errorTime ? '1px solid red' : 'none',
                      },
                    }}
                    highlightToday
                    minDate={new Date()}
                    locale={'ru'}
                    valueFormat={'DD.MM.YYYY'}
                    clearable
                    allowDeselect
                    popoverProps={{
                      zIndex: 100001,
                      middlewares: { flip: false, shift: false },
                    }}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <Label text='Время начала' />
                  <TimeDropdown
                    value={startTime}
                    onChange={(value) => {
                      setStartTime(value);
                      dateAndTimeFormatStart(value);
                    }}
                  />
                </div>
              </div>
              {errorTime && <span style={{ color: 'red' }}>Заполни дату начала</span>}
            </div>

            <div className={styles.dateBox}>
              <div style={{ flex: 1 }}>
                <Label text='Дата завершения' />
                <DateInput
                  value={endDate}
                  onChange={(value) => {
                    setEndDate(value);
                    if (value && !endTime) {
                      setEndTime('00:00');
                      dateAndTimeFormatEnd('00:00', value);
                    } else {
                      dateAndTimeFormatEnd(undefined, value);
                    }
                  }}
                  placeholder={'Не выбрана'}
                  rightSection={<IconCalendar />}
                  styles={{
                    input: {
                      height: '48px',
                      fontSize: '16px',
                      border: 'none',
                    },
                  }}
                  highlightToday
                  minDate={startDate ? dayjs(startDate).toDate() : new Date()}
                  locale={'ru'}
                  valueFormat={'DD.MM.YYYY'}
                  clearable
                  allowDeselect
                  popoverProps={{
                    zIndex: 100001,
                    middlewares: { flip: false, shift: false },
                  }}
                />
              </div>
              <div style={{ flex: 1 }}>
                <Label text='Время завершения' />
                <TimeDropdown
                  value={endTime}
                  onChange={(value) => {
                    setEndTime(value);
                    dateAndTimeFormatEnd(value);
                  }}
                />
              </div>
            </div>
          </div>

          <div className={styles.containerBtn}>
            <div>
              <Button
                theme='outline'
                className={styles.button_cancel}
                onClick={handleCancel}>
                {'Отмена'}
              </Button>
            </div>
            <div>
              <Button
                className={styles.button_approve}
                theme='filed'
                onClick={() => {
                  if (!values.title) {
                    setErrorTitle(true);
                    return;
                  }
                  if (!values.text) {
                    setErrorSubTitle(true);
                    return;
                  }
                  if (!values.start_at) {
                    setErrorTime(true);
                    return;
                  }
                  buttonCreateAlert ? handleSave() : handleUpdate();
                }}>
                {buttonCreateAlert ? (
                  <div>{'Создать оповещение'}</div>
                ) : (
                  <div>{'Сохранить изменения'}</div>
                )}
              </Button>
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
};
